
import React, { useState } from 'react';
import type { LapCrossing } from '../services/raceAnalysis';
import { cloudAnalysisService } from '../services/cloudAnalysisService';
import { renderMarkdown } from '../utils/markdownRenderer';

interface RaceInsightsContext {
  name: string;
  category: string;
}

interface GeminiRaceInsightsProps {
  raceData: RaceInsightsContext;
  raceLapsData: Record<number, LapCrossing[]>;
}

const formatTimeGap = (ms: number): string => {
  if (ms <= 0) return 'Leader';
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `+${totalSeconds.toFixed(1)}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `+${minutes}:${Math.round(seconds).toString().padStart(2, '0')}`;
};

const generatePrompt = (raceData: RaceInsightsContext, raceLapsData: Record<number, LapCrossing[]>): string => {
  let prompt = `Analyze the following cycling race data and provide a summary of the key moments and strategic insights. Focus on major breakaways, changes in leadership, and significant time gaps. Present the analysis in markdown format.\n\n`;
  prompt += `Race Name: ${raceData.name}\n`;
  prompt += `Category: ${raceData.category}\n\n`;
  prompt += `Lap-by-Lap Standings:\n`;

  const lapNumbers = Object.keys(raceLapsData).map(Number).sort((a, b) => a - b);

  lapNumbers.forEach(lapNum => {
    const crossings = raceLapsData[lapNum];
    if (crossings.length > 0) {
      prompt += `\nLap ${lapNum} Finish:\n`;
      const leaderTime = crossings[0].crossingTime;
      const top5 = crossings.slice(0, 5);
      top5.forEach((crossing, index) => {
        const timeGap = formatTimeGap(crossing.crossingTime - leaderTime);
        prompt += `${index + 1}. ${crossing.athleteName} (${timeGap})\n`;
      });
    }
  });

  return prompt;
};


const GeminiRaceInsights: React.FC<GeminiRaceInsightsProps> = ({ raceData, raceLapsData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [insights, setInsights] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  const hasLapData = Object.keys(raceLapsData).length > 0;

  const handleAnalyze = async () => {
    setIsLoading(true);
    setError(null);
    setInsights('');

    const prompt = generatePrompt(raceData, raceLapsData);

    try {
      const text = await cloudAnalysisService.generateAIInsights(prompt);
      setInsights(text);
    } catch (e) {
      console.error(e);
      setError('Failed to get insights from the AI. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!hasLapData) {
      return (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
              <p>No complete laps detected. AI analysis requires at least one lap of race data.</p>
          </div>
      )
  }

  return (
    <div className="flex flex-col">
      <div className="flex-shrink-0 mb-3">
        <button
          onClick={handleAnalyze}
          disabled={isLoading}
          className="w-full px-4 py-2 bg-cyan-500 text-gray-900 font-bold rounded-lg hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/20 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Analyzing...
            </>
          ) : (
            'Analyze Race with Gemini'
          )}
        </button>
      </div>

      <div className="text-gray-300 text-sm pr-2 -mr-2">
        {error && <p className="text-red-400 bg-red-900/50 p-3 rounded-md">{error}</p>}
        {insights ? (
             <div 
                className="prose prose-sm prose-invert max-w-none" 
                dangerouslySetInnerHTML={{ __html: renderMarkdown(insights) }} 
             />
        ) : !isLoading && (
            <div className="flex items-center justify-center h-full text-center text-gray-500">
                <p>Click the button to generate AI-powered insights about the race.</p>
            </div>
        )}
      </div>
    </div>
  );
};

export default GeminiRaceInsights;
